import { State, Action, StateContext, Selector, NgxsOnInit } from "@ngxs/store";
import { UI } from "../interface/ui";
import { Platform } from "@angular/cdk/platform";
import { MatSnackBar } from "@angular/material/snack-bar";
import { SwUpdate, VersionReadyEvent } from "@angular/service-worker";
import { Title, Meta } from "@angular/platform-browser";
import { Injectable } from "@angular/core";
import { filter } from "rxjs/operators";

let win: any;

/** Poll the SW for updates every 30 minutes for long-lived sessions. */
const UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1000;

export class InstallApp {
  static readonly type = "[UI] InstallApp]";
}

export class VerifyInstallPrompt {
  static readonly type = "[UI] VerifyInstallPrompt]";
}

export class WatchAppUpdates {
  static readonly type = "[UI] WatchAppUpdates";
}
@Injectable()
@State<UI>({
  name: "ui",
  defaults: {},
})
export class UIState implements NgxsOnInit {
  window: any;
  /**
   * Single registered handler for `beforeinstallprompt`. The state is a
   * singleton, but we still guard against double-registration so an
   * accidental re-dispatch of `VerifyInstallPrompt` never stacks listeners.
   */
  private installPromptHandler: ((event: Event) => void) | null = null;
  /**
   * Guard so a re-dispatch of `WatchAppUpdates` never starts a second
   * polling loop / second snackbar subscription.
   */
  private updateWatcherStarted = false;

  @Selector()
  static device({ device }: UI) {
    return device;
  }

  @Selector()
  static install({ install }: UI) {
    return install;
  }

  @Selector()
  static share({ share }: UI) {
    return share;
  }

  constructor(
    public platform: Platform,
    private snackBar: MatSnackBar,
    private swUpdate: SwUpdate,
    private titleService: Title,
    private meta: Meta
  ) {}

  async ngxsOnInit({ dispatch }: StateContext<UI>) {
    dispatch(new VerifyInstallPrompt());
    dispatch(new WatchAppUpdates());
  }

  @Action(VerifyInstallPrompt)
  verifyInstallPrompt({ patchState }: StateContext<UI>) {
    win = window.navigator;
    const share = !!win.share;

    if (this.installPromptHandler) {
      window.removeEventListener(
        "beforeinstallprompt",
        this.installPromptHandler
      );
    }
    this.installPromptHandler = (event: Event) => {
      event.preventDefault();
      patchState({ install: event as any, share });
    };
    window.addEventListener("beforeinstallprompt", this.installPromptHandler);
  }

  @Action(InstallApp)
  async installApp({ getState, patchState }: StateContext<UI>) {
    const { install } = getState();
    if (!install) return;
    install.prompt();
    await install.userChoice;
    patchState({ install: null });
  }

  /**
   * Subscribes to `SwUpdate.versionUpdates` and, when a new bundle becomes
   * available, surfaces a snackbar inviting the user to reload. Activation
   * happens only after explicit user consent (avoids interrupting form
   * input or in-flight saves).
   *
   * Also kicks off a periodic `checkForUpdate()` so users with a tab open
   * for hours/days still get notified — Angular's SW only polls on initial
   * load + page navigation by default.
   */
  @Action(WatchAppUpdates)
  watchAppUpdates() {
    if (!this.swUpdate.isEnabled) return;
    if (this.updateWatcherStarted) return;
    this.updateWatcherStarted = true;

    this.swUpdate.versionUpdates
      .pipe(
        filter(
          (event): event is VersionReadyEvent => event.type === "VERSION_READY"
        )
      )
      .subscribe(() => this.promptReload());

    // First check shortly after boot so the watcher isn't waiting until the
    // next interval if a deploy happened during the user's session start.
    setTimeout(() => this.safeCheckForUpdate(), 5_000);
    setInterval(() => this.safeCheckForUpdate(), UPDATE_CHECK_INTERVAL_MS);
  }

  private async safeCheckForUpdate() {
    try {
      await this.swUpdate.checkForUpdate();
    } catch {
      // Network blip — the next interval / page navigation will retry.
    }
  }

  private promptReload() {
    const ref = this.snackBar.open(
      "Hay una nueva versión disponible.",
      "Actualizar",
      {
        // No auto-dismiss: user has to actively choose to reload (or close).
        duration: 0,
        panelClass: ["custom-snackbar"],
      }
    );
    ref.onAction().subscribe(async () => {
      try {
        await this.swUpdate.activateUpdate();
      } finally {
        // Force a clean reload so the new entrypoint + assets win the cache.
        document.location.reload();
      }
    });
  }
}
